import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";

import QRCode from "qrcode";
import logo from "./zulu.png";

// ==============================
// STYLES
// ==============================
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
    fontFamily: "Helvetica",
    color: "#111827",
    backgroundColor: "#ffffff",
  },

  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottom: "2 solid #111827",
    paddingBottom: 12,
    marginBottom: 15,
  },

  logo: {
    width: 60,
    height: 60,
  },

  title: {
    fontSize: 18,
    fontWeight: "bold",
  },

  subtitle: {
    fontSize: 9,
    color: "#6B7280",
    marginTop: 2,
  },

  sectionTitle: {
    fontSize: 11,
    fontWeight: "bold",
    marginBottom: 6,
    padding: 6,
    backgroundColor: "#F3F4F6",
    borderLeft: "3 solid #111827",
  },

  grid: {
    flexDirection: "row",
    justifyContent: "space-between",
    border: "1 solid #E5E7EB",
    padding: 10,
    marginBottom: 10,
  },

  col: { width: "48%" },

  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },

  label: {
    color: "#6B7280",
    width: "45%",
  },

  value: {
    fontWeight: "bold",
    width: "55%",
    textAlign: "right",
  },

  table: {
    width: "100%",
    border: "1 solid #E5E7EB",
    marginTop: 8,
  },

  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#111827",
    padding: 6,
    color: "#fff",
  },

  th: {
    fontSize: 10,
    fontWeight: "bold",
  },

  tr: {
    flexDirection: "row",
    borderTop: "1 solid #E5E7EB",
    padding: 6,
  },

  td: {
    fontSize: 9,
  },

  col1: { width: "45%" },
  col2: { width: "20%" },
  col3: { width: "20%" },
  col4: { width: "15%" },

  summaryBox: {
    marginTop: 12,
    border: "1 solid #E5E7EB",
    padding: 10,
    width: "45%",
    marginLeft: "auto",
    backgroundColor: "#F9FAFB",
  },

  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },

  total: {
    fontWeight: "bold",
    fontSize: 11,
    marginTop: 5,
    borderTop: "1 solid #E5E7EB",
    paddingTop: 5,
  },

  paidBadge: {
    marginTop: 6,
    padding: 5,
    fontSize: 9,
    textAlign: "center",
    backgroundColor: "#DCFCE7",
    color: "#166534",
    fontWeight: "bold",
  },

  pendingBadge: {
    marginTop: 6,
    padding: 5,
    fontSize: 9,
    textAlign: "center",
    backgroundColor: "#FEE2E2",
    color: "#991B1B",
    fontWeight: "bold",
  },

  signatureBox: {
    marginTop: 25,
    borderTop: "1 solid #E5E7EB",
    paddingTop: 15,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
  },

  signatureColumn: {
    width: "60%",
  },

  signatureLabel: {
    fontSize: 9,
    color: "#6B7280",
    marginBottom: 8,
  },

  signatureLine: {
    marginTop: 25,
    borderTop: "1 solid #111827",
    paddingTop: 4,
    fontSize: 9,
    fontWeight: "bold",
    textAlign: "center",
  },

  stampBox: {
    width: 100,
    height: 70,
    border: "1 solid #E5E7EB",
    justifyContent: "center",
    alignItems: "center",
    fontSize: 8,
    color: "#9CA3AF",
  },

  footer: {
    position: "absolute",
    bottom: 20,
    left: 30,
    right: 30,
    flexDirection: "row",
    justifyContent: "space-between",
    borderTop: "1 solid #E5E7EB",
    paddingTop: 8,
    alignItems: "center",
  },

  qr: {
    width: 70,
    height: 70,
  },
});

// ==============================
// DOCUMENT
// ==============================
const ReceiptGenerationPDF = ({ receipt }) => {
  const [qr, setQr] = React.useState("");

  React.useEffect(() => {
    if (!receipt) return;

    // const qrData = JSON.stringify({
    //   receipt_id: receipt.id,
    //   buyer: receipt.buyer_name,
    //   vehicle: `${receipt.make} ${receipt.model}`,
    //   total: receipt.total_amount,
    //   paid: receipt.total_paid,
    //   status: receipt.is_fully_paid ? "PAID" : "PENDING",
    // });

    const qrData = `
ZULUWHISKEY SHIPPING COMPANY

RECEIPT DETAILS

Receipt ID: ${receipt.id}
Buyer: ${receipt.buyer_name}
Vehicle: ${receipt.make} ${receipt.model}

Total: USD ${receipt.total_amount}
Paid: USD ${receipt.total_paid}
Balance: USD ${Number(receipt.total_amount) - Number(receipt.total_paid)}

Status: ${receipt.is_fully_paid ? "PAID" : "PENDING"}
`;

    QRCode.toDataURL(qrData).then(setQr);
  }, [receipt]);

  if (!receipt) return null;

  const remaining =
    Number(receipt.total_amount || 0) -
    Number(receipt.total_paid || 0);

  return (
    <Document>
      <Page size="A4" style={styles.page}>

        {/* HEADER */}
        <View style={styles.header}>
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            <Image src={logo} style={styles.logo} />

            <View style={{ marginLeft: 10 }}>
              <Text style={styles.title}>
                ZULUWHISKEY SHIPPING - CAR PURCHASE RECEIPT
              </Text>
              <Text style={styles.subtitle}>Receipt ID: {receipt.id}</Text>
              <Text style={styles.subtitle}>
                Date: {new Date(receipt.created_at).toLocaleDateString()}
              </Text>
            </View>
          </View>
        </View>

        {/* BUYER INFORMATION */}
<Text style={styles.sectionTitle}>Buyer Information</Text>

<View style={styles.grid}>
  <View style={styles.col}>
    <View style={styles.row}>
      <Text style={styles.label}>Buyer Name:</Text>
      <Text style={styles.value}>{receipt.buyer_name}</Text>
    </View>

    <View style={styles.row}>
      <Text style={styles.label}>Receipt ID:</Text>
      <Text style={styles.value}>{receipt.id}</Text>
    </View>
  </View>

  <View style={styles.col}>
    <View style={styles.row}>
      <Text style={styles.label}>Vehicle:</Text>
      <Text style={styles.value}>
        {receipt.make} {receipt.model}
      </Text>
    </View>

    <View style={styles.row}>
      <Text style={styles.label}>Payment Status:</Text>
      <Text style={styles.value}>
        {receipt.is_fully_paid ? "FULLY PAID" : "BALLANCE PENDING"}
      </Text>
    </View>
  </View>
</View>

        {/* PAYMENT TABLE */}
        <Text style={styles.sectionTitle}>Payment Breakdown</Text>

        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.th, styles.col1]}>Description</Text>
            <Text style={[styles.th, styles.col2]}>Amount</Text>
            <Text style={[styles.th, styles.col3]}>Paid</Text>
            <Text style={[styles.th, styles.col4]}>Balance</Text>
          </View>

          <View style={styles.tr}>
            <Text style={[styles.td, styles.col1]}>Car Purchase</Text>

            <Text style={[styles.td, styles.col2]}>
              $USD {receipt.total_amount}
            </Text>

            <Text style={[styles.td, styles.col3]}>
              $USD {receipt.total_paid}
            </Text>

            <Text style={[styles.td, styles.col4]}>
              $USD {remaining}
            </Text>
          </View>
        </View>

        {/* STATUS */}
        <View style={receipt.is_fully_paid ? styles.paidBadge : styles.pendingBadge}>
          {receipt.is_fully_paid ? "FULLY PAID" : "PAYMENT PENDING"}
        </View>

        {/* SUMMARY */}
        <View style={styles.summaryBox}>
          <View style={styles.summaryRow}>
            <Text>Total Amount:</Text>
            <Text>$USD {receipt.total_amount}</Text>
          </View>

          <View style={styles.summaryRow}>
            <Text>Total Paid:</Text>
            <Text>$USD {receipt.total_paid}</Text>
          </View>

          <View style={[styles.summaryRow, styles.total]}>
            <Text>Remaining:</Text>
            <Text>$USD {remaining}</Text>
          </View>
        </View>

        {/* SIGNATURE */}
        <View style={styles.signatureBox}>
          <View style={styles.signatureColumn}>
            <Text style={styles.signatureLabel}>
              Authorized Management Signature
            </Text>

            <Text style={styles.signatureLine}>
              ________________________________
            </Text>
          </View>

          <View style={styles.stampBox}>OFFICIAL STAMP</View>
        </View>

        {/* FOOTER */}
        <View style={styles.footer}>
          <Text>Zuluwhiskey Car Sales System</Text>
          {qr && <Image src={qr} style={styles.qr} />}
        </View>

      </Page>
    </Document>
  );
};

export default ReceiptGenerationPDF;



// import React from "react";
// import {
//   Document,
//   Page,
//   Text,
//   View,
//   StyleSheet,
//   Image,
// } from "@react-pdf/renderer";

// import QRCode from "qrcode";
// import logo from "./zulu.png";

// // ==============================
// // STYLES
// // ==============================
// const styles = StyleSheet.create({
//   page: {
//     padding: 30,
//     fontSize: 10,
//     fontFamily: "Helvetica",
//     color: "#111827",
//     backgroundColor: "#ffffff",
//   },

//   header: {
//     flexDirection: "row",
//     justifyContent: "space-between",
//     borderBottom: "2 solid #111827",
//     paddingBottom: 12,
//     marginBottom: 15,
//   },

//   logo: {
//     width: 60,
//     height: 60,
//   },

//   title: {
//     fontSize: 18,
//     fontWeight: "bold",
//   },

//   subtitle: {
//     fontSize: 9,
//     color: "#6B7280",
//     marginTop: 2,
//   },

//   sectionTitle: {
//     fontSize: 11,
//     fontWeight: "bold",
//     marginBottom: 6,
//     padding: 6,
//     backgroundColor: "#F3F4F6",
//     borderLeft: "3 solid #111827",
//   },

//   grid: {
//     flexDirection: "row",
//     justifyContent: "space-between",
//     border: "1 solid #E5E7EB",
//     padding: 10,
//     marginBottom: 10,
//   },

//   col: { width: "48%" },

//   row: {
//     flexDirection: "row",
//     justifyContent: "space-between",
//     marginBottom: 4,
//   },

//   label: {
//     color: "#6B7280",
//     width: "45%",
//   },

//   value: {
//     fontWeight: "bold",
//     width: "55%",
//     textAlign: "right",
//   },

//   table: {
//     width: "100%",
//     border: "1 solid #E5E7EB",
//     marginTop: 8,
//   },

//   tableHeader: {
//     flexDirection: "row",
//     backgroundColor: "#111827",
//     padding: 6,
//     color: "#fff",
//   },

//   th: {
//     fontSize: 10,
//     fontWeight: "bold",
//   },

//   tr: {
//     flexDirection: "row",
//     borderTop: "1 solid #E5E7EB",
//     padding: 6,
//   },

//   td: {
//     fontSize: 9,
//   },

//   col1: { width: "45%" },
//   col2: { width: "20%" },
//   col3: { width: "20%" },
//   col4: { width: "15%" },

//   summaryBox: {
//     marginTop: 12,
//     border: "1 solid #E5E7EB",
//     padding: 10,
//     width: "45%",
//     marginLeft: "auto",
//     backgroundColor: "#F9FAFB",
//   },

//   summaryRow: {
//     flexDirection: "row",
//     justifyContent: "space-between",
//     marginBottom: 4,
//   },

//   total: {
//     fontWeight: "bold",
//     fontSize: 11,
//     marginTop: 5,
//     borderTop: "1 solid #E5E7EB",
//     paddingTop: 5,
//   },

//   paidBadge: {
//     marginTop: 6,
//     padding: 5,
//     fontSize: 9,
//     textAlign: "center",
//     backgroundColor: "#DCFCE7",
//     color: "#166534",
//     fontWeight: "bold",
//   },

//   pendingBadge: {
//     marginTop: 6,
//     padding: 5,
//     fontSize: 9,
//     textAlign: "center",
//     backgroundColor: "#FEE2E2",
//     color: "#991B1B",
//     fontWeight: "bold",
//   },

//   // ==========================
//   // SIGNATURE AREA
//   // ==========================
//   signatureBox: {
//     marginTop: 25,
//     borderTop: "1 solid #E5E7EB",
//     paddingTop: 15,
//     flexDirection: "row",
//     justifyContent: "space-between",
//     alignItems: "flex-end",
//   },

//   signatureColumn: {
//     width: "60%",
//   },

//   signatureLabel: {
//     fontSize: 9,
//     color: "#6B7280",
//     marginBottom: 8,
//   },

//   signatureLine: {
//     marginTop: 25,
//     borderTop: "1 solid #111827",
//     paddingTop: 4,
//     fontSize: 9,
//     fontWeight: "bold",
//     textAlign: "center",
//   },

//   stampBox: {
//     width: 100,
//     height: 70,
//     border: "1 solid #E5E7EB",
//     justifyContent: "center",
//     alignItems: "center",
//     fontSize: 8,
//     color: "#9CA3AF",
//   },

//   footer: {
//     position: "absolute",
//     bottom: 20,
//     left: 30,
//     right: 30,
//     flexDirection: "row",
//     justifyContent: "space-between",
//     borderTop: "1 solid #E5E7EB",
//     paddingTop: 8,
//     alignItems: "center",
//   },

//   qr: {
//     width: 70,
//     height: 70,
//   },
// });

// // ==============================
// // DOCUMENT
// // ==============================
// const ReceiptGenerationPDF = ({ receipt }) => {
//   const [qr, setQr] = React.useState("");

//   React.useEffect(() => {
//     if (!receipt) return;

//     const qrData = JSON.stringify({
//       receipt_id: receipt.id,
//       buyer: receipt.buyer_name,
//       vehicle: `${receipt.make} ${receipt.model}`,
//       total: receipt.total_amount,
//       paid: receipt.total_paid,
//       status: receipt.is_fully_paid ? "PAID" : "PENDING",
//     });

//     QRCode.toDataURL(qrData).then(setQr);
//   }, [receipt]);

//   if (!receipt) return null;

//   const remaining =
//     Number(receipt.total_amount || 0) -
//     Number(receipt.total_paid || 0);

//   return (
//     <Document>
//       <Page size="A4" style={styles.page}>

//         {/* HEADER */}
//         <View style={styles.header}>
//           <View style={{ flexDirection: "row", alignItems: "center" }}>
//             <Image src={logo} style={styles.logo} />

//             <View style={{ marginLeft: 10 }}>
//               <Text style={styles.title}>
//                 ZULUWHISKEY SHIPPING - CAR PURCHASE RECEIPT
//               </Text>
//               <Text style={styles.subtitle}>
//                 Receipt ID: {receipt.id}
//               </Text>
//               <Text style={styles.subtitle}>
//                 Date: {new Date(receipt.created_at).toLocaleDateString()}
//               </Text>
//             </View>
//           </View>
//         </View>

//         {/* BUYER + VEHICLE */}
//         <View style={styles.grid}>

//           {/* BUYER */}
//           <View style={styles.col}>
//             <Text style={styles.sectionTitle}>Buyer Information</Text>

//             <View style={styles.row}>
//               <Text style={styles.label}>Name</Text>
//               <Text style={styles.value}>{receipt.buyer_name}</Text>
//             </View>

//             <View style={styles.row}>
//               <Text style={styles.label}>Contact</Text>
//               <Text style={styles.value}>{receipt.buyer_phone}</Text>
//             </View>
//           </View>

//           {/* VEHICLE */}
//           <View style={styles.col}>
//             <Text style={styles.sectionTitle}>Vehicle Details</Text>

//             <View style={styles.row}>
//               <Text style={styles.label}>Make</Text>
//               <Text style={styles.value}>{receipt.make}</Text>
//             </View>

//             <View style={styles.row}>
//               <Text style={styles.label}>Model</Text>
//               <Text style={styles.value}>{receipt.model}</Text>
//             </View>

//             <View style={styles.row}>
//               <Text style={styles.label}>Year</Text>
//               <Text style={styles.value}>{receipt.year}</Text>
//             </View>
//           </View>
//         </View>

//         {/* PAYMENT TABLE */}
//         <Text style={styles.sectionTitle}>Payment Breakdown</Text>

//         <View style={styles.table}>
//           <View style={styles.tableHeader}>
//             <Text style={[styles.th, styles.col1]}>Description</Text>
//             <Text style={[styles.th, styles.col2]}>Amount</Text>
//             <Text style={[styles.th, styles.col3]}>Paid</Text>
//             <Text style={[styles.th, styles.col4]}>Balance</Text>
//           </View>

//           <View style={styles.tr}>
//             <Text style={[styles.td, styles.col1]}>
//               Car Purchase
//             </Text>
//             <Text style={[styles.td, styles.col2]}>
//               ${receipt.total_amount}
//             </Text>
//             <Text style={[styles.td, styles.col3]}>
//               ${receipt.total_paid}
//             </Text>
//             <Text style={[styles.td, styles.col4]}>
//               ${remaining}
//             </Text>
//           </View>
//         </View>

//         {/* STATUS */}
//         <View
//           style={
//             receipt.is_fully_paid
//               ? styles.paidBadge
//               : styles.pendingBadge
//           }
//         >
//           {receipt.is_fully_paid
//             ? "FULLY PAID"
//             : "PAYMENT PENDING"}
//         </View>

//         {/* SUMMARY */}
//         <View style={styles.summaryBox}>
//           <View style={styles.summaryRow}>
//             <Text>Total Amount:</Text>
//             <Text>${receipt.total_amount}</Text>
//           </View>

//           <View style={styles.summaryRow}>
//             <Text>Total Paid:</Text>
//             <Text>${receipt.total_paid}</Text>
//           </View>

//           <View style={[styles.summaryRow, styles.total]}>
//             <Text>Remaining:</Text>
//             <Text>${remaining}</Text>
//           </View>
//         </View>

//         {/* ==========================
//             SIGNATURE SECTION (NEW)
//         =========================== */}
//         <View style={styles.signatureBox}>

//           <View style={styles.signatureColumn}>
//             <Text style={styles.signatureLabel}>
//               Authorized Management Signature
//             </Text>

//             <Text style={styles.signatureLine}>
//               ________________________________
//             </Text>

            
//           </View>

//           <View style={styles.stampBox}>
//             OFFICIAL STAMP
//           </View>

//         </View>

//         {/* FOOTER */}
//         <View style={styles.footer}>
//           <Text>Zuluwhiskey Car Sales System</Text>
//           {qr && <Image src={qr} style={styles.qr} />}
//         </View>

//       </Page>
//     </Document>
//   );
// };

// export default ReceiptGenerationPDF;